/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallAndPoll } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { FileType } from 'src/core/types/FileType';
import { ResponseResourcesValue } from 'src/core/types/genericApiResponse';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { QnaMakerQnaSwagger, QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSourcesSwaggers } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';
import { qnaMakerGenericFailureError } from 'src/qnaMaker/core/utils/qnaMakerOperation';

/**
 * Represents a service for manipulating QnA Maker QnAs objects.
 */
export const getQnaMakerAssetsService = createService(({ context, getEnvironment }) => {
    const { apiUrl } = getEnvironment({ apiName: 'qna' });

    type QnaAssetsSwagger = QnaMakerQnaSwagger | QnaMakerSourcesSwaggers | QnaMakerSynonymSwagger;

    type AssetsType = 'qnas' | 'synonyms';

    const getDefaultApiOptions = () =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, true)
            .build();

    const getDefaultUrl = (id?: string, assetsType: AssetsType = 'qnas') =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(id)
            .resource(assetsType)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion);

    const getAssets = async ({
        projectId,
        assetsType = 'qnas'
    }: {
        projectId: string;
        assetsType?: AssetsType;
    }): Promise<QnaAssetsSwagger[]> => {
        const url = getDefaultUrl(projectId, assetsType).build();

        const { data: items } = await httpService.get<ResponseResourcesValue<QnaAssetsSwagger>>(url, getDefaultApiOptions());

        return items.value;
    };

    const updateAssets = async ({
        projectId,
        assetWithOperations,
        assetsType = 'qnas'
    }: {
        projectId: string;
        assetWithOperations: QnaPatchOperation<Partial<QnaMakerQnaSwagger>>[] | QnaMakerSynonymSwagger[];
        assetsType?: AssetsType;
    }): Promise<void> => {
        const url = getDefaultUrl(projectId, assetsType).build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (jobId: string) =>
            getDefaultUrl(projectId)
                .resource('jobs', jobId)
                .build();

        if (assetsType === 'qnas') {
            const pathOp = httpService.patch<void>(url, assetWithOperations, getDefaultApiOptions());

            await createOperationCallAndPoll(
                pathOp,
                getDefaultApiOptions(),
                {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                    handleCall: res => {
                        if (res.status === 'failed') {
                            let err;
                            if (res.errors?.length) {
                                err = new Error(
                                    res.errors[0].message.concat(`\n ${res.errors[0].details?.map(d => `- ${d.code}: ${d.message}\n`)}`)
                                );
                                err.name = res.errors[0].code;
                            } else {
                                err = qnaMakerGenericFailureError(res.jobId);
                            }

                            throw err;
                        }
                    }
                },
                // ToDo: will be revisited once the backend returns the whole URI in the location header
                response =>
                    getJobStatusUrl(
                        response.headers[LuisConstants.OperationHeaders.LocationHeader]
                            .split('/')
                            .pop()
                            .split('?')[0]
                    ),
                LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
            );
        } else {
            await httpService.put<void>(url, { value: [...assetWithOperations] }, getDefaultApiOptions());
        }
    };

    const importAssets = async ({
        projectId,
        fileType,
        fileUri,
        assetsType = 'qnas'
    }: {
        projectId: string;
        fileType: Extract<FileType, 'xlsx' | 'tsv'>;
        fileUri: string;
        assetsType?: AssetsType;
    }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectId)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .resource(':import')
            .queryParam('assetKind', assetsType)
            .queryParam('format', fileType === 'xlsx' ? 'excel' : fileType)
            .build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (jobId: string) =>
            urlBuilder()
                .baseUrl(apiUrl)
                .project(projectId)
                .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
                .resource('import')
                .resource('jobs', jobId)
                .build();

        const pathOp = httpService.post<void>(url, { fileUri }, getDefaultApiOptions());

        await createOperationCallAndPoll<{ resultUrl: string } & OperationStatusBaseSwagger>(
            pathOp,
            getDefaultApiOptions(),
            {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status.toLowerCase() !== 'succeeded' && res.data.status.toLowerCase() !== 'failed',
                handleCall: res => {
                    if (res.status === 'failed') {
                        let err;
                        if (res.errors?.length) {
                            err = new Error(
                                res.errors[0].message.concat(`\n ${res.errors[0].details?.map(d => `- ${d.code}: ${d.message}\n`)}`)
                            );
                            err.name = res.errors[0].code;
                        } else {
                            err = qnaMakerGenericFailureError(res.jobId);
                        }

                        throw err;
                    }
                }
            },
            // ToDo: will be revisited once the backend returns the whole URI in the location header
            response =>
                getJobStatusUrl(
                    response.headers[LuisConstants.OperationHeaders.LocationHeader]
                        .split('/')
                        .pop()
                        .split('?')[0]
                ),
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );
    };

    const exportAssets = async ({
        projectId,
        fileType,
        assetsType = 'qnas'
    }: {
        projectId: string;
        fileType: Extract<FileType, 'xlsx' | 'tsv'>;
        assetsType?: AssetsType;
    }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectId)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .resource(':export')
            .queryParam('assetKind', assetsType)
            .queryParam('format', fileType === 'xlsx' ? 'excel' : fileType)
            .build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (_jobId: string) =>
            urlBuilder()
                .baseUrl(apiUrl)
                .project(projectId)
                .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
                .resource('export')
                .resource('jobs', _jobId)
                .build();

        const pathOp = httpService.post<void>(url, {}, getDefaultApiOptions());

        let jobId;
        await createOperationCallAndPoll<{ resultUrl: string } & OperationStatusBaseSwagger>(
            pathOp,
            getDefaultApiOptions(),
            {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: res => {
                    jobId = res.jobId;

                    if (res.status === 'failed') {
                        let err;
                        if (res.errors?.length) {
                            err = new Error(
                                res.errors[0].message.concat(`\n ${res.errors[0].details?.map(d => `- ${d.code}: ${d.message}\n`)}`)
                            );
                            err.name = res.errors[0].code;
                        } else {
                            err = qnaMakerGenericFailureError(jobId);
                        }

                        throw err;
                    }
                },
                retryStrategy: res => res.data.status.toLowerCase() !== 'succeeded' && res.data.status.toLowerCase() !== 'failed'
            },
            // ToDo: will be revisited once the backend returns the whole URI in the location header
            response =>
                getJobStatusUrl(
                    response.headers[LuisConstants.OperationHeaders.LocationHeader]
                        .split('/')
                        .pop()
                        .split('?')[0]
                ),
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );

        const resultUrl = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectId)
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .resource('export')
            .resource('jobs', jobId)
            .resource('result')
            .build();

        await fileService.download(
            resultUrl,
            `${projectId}${assetsType === 'qnas' ? '_qnas' : '_synonyms'}.${fileType}`,
            getDefaultApiOptions()
        );
    };

    const submitSmartSuggestionsFeedback = async (_: { feedbackText: string; isPositive: boolean }): Promise<void> => {
        // @TODO
    };

    return {
        getAssets,
        updateAssets,
        importAssets,
        exportAssets,
        submitSmartSuggestionsFeedback
    };
});
